import type { AuthTokens } from '@/app/api/refresh';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { LoginFactorResponse } from './auth/api';

export type CabinetType = 'sellerOrBuyer' | 'factor';
export enum CabinetFlow {
  COMMON = 'common',
  NBU = 'nbu',
}

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  profile?: LoginFactorResponse['data'];
  userKey?: string | null;
  cabinetType?: CabinetType;
  phisPersonPinfl?: string;
  cabinetFlow?: CabinetFlow;
  mfo?: string;
  isUsbLog?: boolean;
  refreshToken: string | null;
}

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  userKey: localStorage.getItem('userKey'),
  isAuthenticated:
    !!localStorage.getItem('token') && !!localStorage.getItem('userKey'),
  profile: localStorage.getItem('profile')
    ? JSON.parse((localStorage.getItem('profile') as string) || '{}')
    : undefined,
  cabinetType: localStorage.getItem('cabinetType')
    ? JSON.parse(localStorage.getItem('cabinetType') || '{}')
    : undefined,
  cabinetFlow: localStorage.getItem('cabinetFlow')
    ? JSON.parse(localStorage.getItem('cabinetFlow') || '{}')
    : undefined,
  mfo: localStorage.getItem('mfo')
    ? localStorage.getItem('mfo') || ''
    : undefined,
  isUsbLog: localStorage.getItem('isUsbLog')
    ? localStorage.getItem('isUsbLog') === 'true'
    : undefined,
  refreshToken: localStorage.getItem('refreshToken'),
};

const NBU_TINS = ['200836354', '311629927'];

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<
        LoginFactorResponse & {
          mfo?: string;
          token: string;
          userKey: string;
          cabinetType: CabinetType;
          phisPersonPinfl?: string;
          isUsbLog?: boolean;
          refreshToken?: string;
        }
      >
    ) => {
      const profile = {
        ...action.payload.data,
        tin: action.payload.data.tin || '',
      };
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.profile = profile;
      state.userKey = action.payload.userKey;
      state.cabinetType = action.payload.cabinetType;
      state.phisPersonPinfl = action.payload.phisPersonPinfl;
      state.mfo = action.payload.mfo;
      state.isUsbLog = action.payload.isUsbLog;
      state.refreshToken = action.payload.refreshToken || null;
      // tin === 200836354
      const isNbu = NBU_TINS.includes(profile.tin);
      if (isNbu) {
        state.cabinetFlow = CabinetFlow.NBU;
      } else {
        state.cabinetFlow = CabinetFlow.COMMON;
      }

      localStorage.setItem('profile', JSON.stringify(profile));
      localStorage.setItem(
        'cabinetType',
        JSON.stringify(action.payload.cabinetType)
      );
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('userKey', action.payload.userKey);
      localStorage.setItem('cabinetFlow', JSON.stringify(state.cabinetFlow));
      if (action.payload.phisPersonPinfl) {
        localStorage.setItem('phisPersonPinfl', action.payload.phisPersonPinfl);
      }
      localStorage.setItem('mfo', action.payload.mfo || '');
      if (action.payload.isUsbLog) {
        localStorage.setItem('isUsbLog', 'true');
      } else {
        localStorage.removeItem('isUsbLog');
      }
      if (action.payload.refreshToken) {
        localStorage.setItem('refreshToken', action.payload.refreshToken);
      } else {
        localStorage.removeItem('refreshToken');
      }
    },
    // Обновление пары токенов после POST /auth/refresh
    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;

      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    logout: (state) => {
      state.token = null;
      state.isAuthenticated = false;
      state.profile = undefined;
      state.refreshToken = null;
      localStorage.removeItem('token');
      localStorage.removeItem('userKey');
      localStorage.removeItem('profile');
      localStorage.removeItem('cabinetType');
      localStorage.removeItem('phisPersonPinfl');
      localStorage.removeItem('cabinetFlow');
      localStorage.removeItem('mfo');
      localStorage.removeItem('isUsbLog');
      localStorage.removeItem('refreshToken');
    },
  },
});

export const { setCredentials, logout, setTokens } = authSlice.actions;

export default authSlice.reducer;
